/** @format */

import { useEffect } from 'react';

const prefersReducedMotion = () =>
	typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Advances a horizontally scrollable strip one item at a time and rewinds at the end.
 * It is a no-op while every item fits, pauses after user interaction and respects reduced motion.
 */
export default function useAutoScroll(ref, { interval = 3000, resumeDelay = 5000 } = {}) {
	useEffect(() => {
		const strip = ref.current;
		if (!strip || prefersReducedMotion()) return undefined;

		let isPaused = false;
		let resumeTimer = null;
		const pause = () => {
			isPaused = true;
			clearTimeout(resumeTimer);
			resumeTimer = setTimeout(() => {
				isPaused = false;
			}, resumeDelay);
		};

		const tick = () => {
			if (isPaused) return;

			const maxScroll = strip.scrollWidth - strip.clientWidth;
			if (maxScroll <= 1) return;

			if (strip.scrollLeft >= maxScroll - 1) {
				strip.scrollTo({ left: 0, behavior: 'smooth' });
				return;
			}

			const itemWidth = strip.firstElementChild ? strip.firstElementChild.offsetWidth : strip.clientWidth;
			strip.scrollBy({ left: itemWidth, behavior: 'smooth' });
		};

		const timer = setInterval(tick, interval);
		const interactionEvents = ['touchstart', 'pointerdown', 'mouseenter', 'wheel'];
		interactionEvents.forEach((event) => strip.addEventListener(event, pause, { passive: true }));

		return () => {
			clearInterval(timer);
			clearTimeout(resumeTimer);
			interactionEvents.forEach((event) => strip.removeEventListener(event, pause));
		};
	}, [ref, interval, resumeDelay]);
}
